import { FC, useCallback, useEffect, useMemo } from 'react';
import intl from 'react-intl-universal';
import { Checkbox, Dropdown, FormItem } from '@getsynapse/design-system';
import { useProjectPrograms } from 'Pages/ProjectsListPage/hooks/useProjectPrograms/useProjectPrograms';
import { Option } from 'utils/customTypes';

interface ProjectProgramFilterProps {
  onSelect: (selected: string[]) => void;
  onSelectWithoutPrograms: (selected: boolean) => void;
  selected?: string[];
  withoutPrograms?: boolean;
}

const ProjectProgramFilter: FC<ProjectProgramFilterProps> = ({
  onSelect,
  onSelectWithoutPrograms,
  selected = [],
  withoutPrograms = false,
}) => {
  const { programs, fetchProjectPrograms } = useProjectPrograms();

  useEffect(() => {
    fetchProjectPrograms();
  }, [fetchProjectPrograms]);

  const options = useMemo(
    () =>
      programs.map((program) => ({
        label: program.title,
        value: program.id,
      })),
    [programs]
  );

  const selectedPrograms = useMemo(
    () => options.filter((option) => selected.includes(option.value)),
    [options, selected]
  );

  const handleChange = useCallback(
    (newOptions: Option[]) => {
      const selectedIds = newOptions.map((option) => option.value);
      onSelect(selectedIds);
      if (selectedIds.length > 0 && withoutPrograms) {
        onSelectWithoutPrograms(false);
      }
    },
    [onSelect, onSelectWithoutPrograms, withoutPrograms]
  );

  return (
    <div className='flex flex-col space-y-2'>
      <FormItem label={intl.get('PROJECT_DETAIL.PROGRAMS')}>
        <Dropdown
          options={options}
          values={selectedPrograms}
          onChange={handleChange}
          multiple
          disabled={withoutPrograms}
          placeholder={intl.get('FILTER_GENERAL.PLACEHOLDER')}
          triggerProps={{
            'aria-label': 'project-program-filter',
          }}
        />
      </FormItem>
      <Checkbox
        label={intl.get('PROJECTS_LIST_PAGE.FILTERS.WITHOUT_PROGRAMS')}
        checked={withoutPrograms}
        onChange={(event) => {
          const isChecked = event.target.checked;
          if (isChecked) {
            onSelect([]);
          }
          onSelectWithoutPrograms(isChecked);
        }}
      />
    </div>
  );
};

export default ProjectProgramFilter;
